import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useEffect, useEffectEvent, useRef } from "react";
import type {
  ArtifactSourceRow,
  ArtifactSourceState,
  ArtifactThemeMode,
  ErrorMessage,
  HostMessage,
} from "@entrilia/shared/artifactRuntime/protocol";
import { isRuntimeMessage, MIN_CAPTURE_WIDTH_PX } from "@entrilia/shared/artifactRuntime/protocol";
import type { SmartViewArtifactVersion } from "../../../../../api/types/smartViewArtifactsTypes";
import type { ArtifactSourceStore } from "./artifactSourceStore";
import type {
  ArtifactCheckResult,
  ArtifactCheckStop,
  ArtifactCompareSkip,
  ArtifactPageProblem,
  ArtifactRuntimeError,
  ArtifactSourceNote,
} from "./checkResult";
import { CHECK_STEP_TIMEOUT_MS, findSourceNote, findViewTitle, needsFix, toRuntimeError } from "./checkResult";
import { createInitMessage } from "./frameInit";
import type { ViewWalkStep } from "./viewWalk";
import { buildViewWalk } from "./viewWalk";

interface Props {
  sourceStore: ArtifactSourceStore;
  artifactId: string;
  version: SmartViewArtifactVersion;
  filterValues: Record<string, string[]>;
  filterLabels: Record<string, Record<string, string>>;
  mode: ArtifactThemeMode;
  logo?: string;
  getCompareMode: (version: number) => ArtifactThemeMode | undefined;
  onFinished: (result: ArtifactCheckResult) => void;
  onPreviewCaptured: (artifactId: string, version: number, image: Blob) => void;
}

interface RenderedWait {
  view: string;
  mode: ArtifactThemeMode;
  settle: (isRendered: boolean) => void;
}

// Chrome throttles rendering in off-screen cross-origin frames, so the checker stays in place, invisible.
const checkerSx: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  minWidth: MIN_CAPTURE_WIDTH_PX,
  border: 0,
  opacity: 0,
  pointerEvents: "none",
};

const PREVIEW_CAPTURE_TIMEOUT_MS = 10_000;
const COMPARE_VISIBLE_TIMEOUT_MS = 10 * 60_000;
const COMPARE_CAPTURE_TIMEOUT_MS = 30_000;

const getOtherMode = (mode: ArtifactThemeMode): ArtifactThemeMode => (mode === "light" ? "dark" : "light");

const ArtifactCheckerFrame = ({
  sourceStore,
  artifactId,
  version,
  filterValues,
  filterLabels,
  mode,
  logo,
  getCompareMode,
  onFinished,
  onPreviewCaptured,
}: Props) => {
  const frameRef = useRef<HTMLIFrameElement>(null);

  const createInit = useEffectEvent((initMode: ArtifactThemeMode) =>
    createInitMessage(version, filterValues, filterLabels, initMode, { build: true, drillMode: false }, logo)
  );
  const getCurrentMode = useEffectEvent(() => mode);
  const buildWalk = useEffectEvent((loadedRows: ReadonlyMap<string, ArtifactSourceRow[]>): ViewWalkStep[] =>
    buildViewWalk(version, loadedRows)
  );
  const createError = useEffectEvent((message: ErrorMessage) => toRuntimeError(version, message));
  const getViewTitle = useEffectEvent((view: string) => findViewTitle(version, view));
  const createSourceNote = useEffectEvent((sourceKey: string, state: ArtifactSourceState, view: string) =>
    findSourceNote(version, sourceKey, state, view)
  );
  const readCompareMode = useEffectEvent(getCompareMode);
  const handleFinished = useEffectEvent(onFinished);
  const handlePreviewCaptured = useEffectEvent(onPreviewCaptured);

  useEffect(() => {
    const frameWindow = frameRef.current?.contentWindow;
    if (!frameWindow) {
      return;
    }
    const versionNumber = version.version;
    const secondMode = getCurrentMode();
    const firstMode = getOtherMode(secondMode);
    const initial = createInit(firstMode);
    const sourceKeys = new Map<string, string>();
    const loadedRows = new Map<string, ArtifactSourceRow[]>();
    const errors: ArtifactRuntimeError[] = [];
    const problems: ArtifactPageProblem[] = [];
    const sourceNotes: ArtifactSourceNote[] = [];
    const warnings = new Set<string>();
    let currentView = "";
    let stop: ArtifactCheckStop | undefined;
    let pendingWait: RenderedWait | undefined;
    let settlePreview: ((image: Blob | undefined) => void) | undefined;
    let settleVisibility: ((isVisible: boolean) => void) | undefined;
    let settleCompare: ((image: Blob | undefined) => void) | undefined;
    let compareRender: Blob | undefined;
    let compareSkip: ArtifactCompareSkip | undefined;
    let isStarted = false;
    let isStopped = false;
    let isCancelled = false;

    const post = (message: HostMessage): void => {
      frameWindow.postMessage(message, location.origin);
    };

    const client = sourceStore.connect({
      artifactId,
      version: versionNumber,
      sources: initial.sources,
      filterValues: initial.filterValues,
      onSourceState: (subscriptionId, state) => {
        const sourceKey = sourceKeys.get(subscriptionId);
        if (sourceKey !== undefined && state.status === "ready") {
          loadedRows.set(sourceKey, state.rows);
        }
        const note = sourceKey === undefined ? undefined : createSourceNote(sourceKey, state, currentView);
        if (note && !sourceNotes.some(({ sourceKey: key, kind }) => key === note.sourceKey && kind === note.kind)) {
          sourceNotes.push(note);
        }
        post({ type: "sourceState", version: versionNumber, subscriptionId, state });
      },
    });

    const waitForRendered = (view: string, waitMode: ArtifactThemeMode): Promise<boolean> =>
      new Promise((resolve) => {
        if (isStopped) {
          resolve(false);
          return;
        }
        const settle = (isRendered: boolean): void => {
          clearTimeout(timer);
          pendingWait = undefined;
          resolve(isRendered);
        };
        const timer = setTimeout(() => {
          stop = { reason: "timedOut", view, viewTitle: getViewTitle(view), mode: waitMode };
          settle(false);
        }, CHECK_STEP_TIMEOUT_MS);
        pendingWait = { view, mode: waitMode, settle };
      });

    const openView = (
      viewKey: string,
      params: Record<string, string[]>,
      waitMode: ArtifactThemeMode
    ): Promise<boolean> => {
      currentView = viewKey;
      post({ type: "openView", version: versionNumber, viewKey, params });
      return waitForRendered(viewKey, waitMode);
    };

    const walkViews = async (walkMode: ArtifactThemeMode): Promise<boolean> => {
      const openedViews = new Set<string>();
      const findNextStep = (): ViewWalkStep | undefined =>
        buildWalk(loadedRows).find(({ viewKey }) => !openedViews.has(viewKey));
      let step = findNextStep();
      while (step) {
        openedViews.add(step.viewKey);
        if (!(await openView(step.viewKey, step.params, walkMode))) {
          return false;
        }
        step = findNextStep();
      }
      return openedViews.size === 0 || openView("", {}, walkMode);
    };

    const capturePreview = (): Promise<void> =>
      new Promise((resolve) => {
        if (isStopped) {
          resolve();
          return;
        }
        const settle = (image: Blob | undefined): void => {
          clearTimeout(timer);
          settlePreview = undefined;
          if (image && !isCancelled) {
            handlePreviewCaptured(artifactId, versionNumber, image);
          }
          resolve();
        };
        const timer = setTimeout(() => settle(undefined), PREVIEW_CAPTURE_TIMEOUT_MS);
        settlePreview = settle;
        post({ type: "capturePreview", version: versionNumber });
      });

    const switchMode = (nextMode: ArtifactThemeMode): Promise<boolean> => {
      post({ type: "themeChanged", version: versionNumber, mode: nextMode });
      return waitForRendered("", nextMode);
    };

    // A hidden tab does not paint chart canvases, so the render waits for a visible one.
    const waitUntilVisible = (): Promise<boolean> =>
      new Promise((resolve) => {
        if (isStopped) {
          resolve(false);
          return;
        }
        if (document.visibilityState === "visible") {
          resolve(true);
          return;
        }
        const settle = (isVisible: boolean): void => {
          clearTimeout(timer);
          settleVisibility = undefined;
          resolve(isVisible);
        };
        const timer = setTimeout(() => settle(false), COMPARE_VISIBLE_TIMEOUT_MS);
        settleVisibility = settle;
      });

    const captureCompare = (): Promise<Blob | undefined> =>
      new Promise((resolve) => {
        if (isStopped) {
          resolve(undefined);
          return;
        }
        const settle = (image: Blob | undefined): void => {
          clearTimeout(timer);
          settleCompare = undefined;
          resolve(image);
        };
        const timer = setTimeout(() => settle(undefined), COMPARE_CAPTURE_TIMEOUT_MS);
        settleCompare = settle;
        post({ type: "captureCompare", version: versionNumber });
      });

    const takeCompare = async (): Promise<void> => {
      if (isStopped || stop || needsFix({ errors, problems, warnings: [] })) {
        return;
      }
      const compareMode = readCompareMode(versionNumber);
      if (!compareMode) {
        return;
      }
      if (compareMode !== secondMode && !(await switchMode(compareMode))) {
        compareSkip = "failed";
        return;
      }
      if (!(await waitUntilVisible())) {
        compareSkip = "hidden";
        return;
      }
      compareRender = await captureCompare();
      if (!compareRender) {
        compareSkip = "failed";
      }
    };

    const finish = (): void => {
      if (!isCancelled) {
        handleFinished({
          version: versionNumber,
          errors,
          problems,
          warnings: [...warnings],
          sourceNotes,
          stop,
          compareRender,
          compareSkip,
        });
      }
    };

    const runCheck = async (): Promise<void> => {
      post(createInit(firstMode));
      const isFirstModeRendered = await waitForRendered("", firstMode);
      if (isFirstModeRendered && firstMode === "light") {
        await capturePreview();
      }
      const isFirstModeChecked = isFirstModeRendered && (await walkViews(firstMode));
      if (isFirstModeChecked && (await switchMode(secondMode))) {
        if (secondMode === "light") {
          await capturePreview();
        }
        await walkViews(secondMode);
      }
      await takeCompare();
      finish();
    };

    const readyTimer = setTimeout(() => {
      isStarted = true;
      isStopped = true;
      stop = { reason: "notStarted" };
      finish();
    }, CHECK_STEP_TIMEOUT_MS);

    const handleMessage = (event: MessageEvent): void => {
      if (event.source !== frameWindow || event.origin !== location.origin || !isRuntimeMessage(event.data)) {
        return;
      }
      const message = event.data;
      if (message.type === "ready") {
        if (!isStarted) {
          isStarted = true;
          clearTimeout(readyTimer);
          runCheck();
        }
        return;
      }
      if (message.version !== versionNumber) {
        return;
      }
      switch (message.type) {
        case "subscribe":
          sourceKeys.set(message.subscriptionId, message.key);
          client.subscribe(message.subscriptionId, message.key, message.overrides);
          return;
        case "unsubscribe":
          sourceKeys.delete(message.subscriptionId);
          client.unsubscribe(message.subscriptionId);
          return;
        case "rendered":
          message.problems.forEach((problem) =>
            problems.push({
              message: problem,
              view: message.view,
              viewTitle: getViewTitle(message.view),
              mode: message.mode,
            })
          );
          if (pendingWait?.view === message.view && pendingWait.mode === message.mode) {
            pendingWait.settle(true);
          }
          return;
        case "error":
          errors.push(createError(message));
          if (!message.contained) {
            pendingWait?.settle(true);
          }
          return;
        case "previewCaptured":
          settlePreview?.(message.image);
          return;
        case "compareCaptured":
          settleCompare?.(message.image);
          return;
        case "warning":
          warnings.add(message.message);
          return;
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        settleVisibility?.(true);
      }
    };

    window.addEventListener("message", handleMessage);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      isCancelled = true;
      isStopped = true;
      clearTimeout(readyTimer);
      pendingWait?.settle(false);
      settlePreview?.(undefined);
      settleVisibility?.(false);
      settleCompare?.(undefined);
      client.close();
    };
  }, [artifactId, version.version, sourceStore]);

  return (
    <Box
      component="iframe"
      ref={frameRef}
      src="/artifact-frame.html"
      title="Dashboard check"
      aria-hidden
      inert
      sx={checkerSx}
    />
  );
};

export default ArtifactCheckerFrame;
